import type { CalendarItem } from "./calendarTypes";
import { parseDateTime } from "./calendarDate";
import { normalizeRoomName } from "./calendarMatchRooms";

export { normalizeRoomName, roomsMatch } from "./calendarMatchRooms";

/** Max minutes Google may start before the dom_event (voorbereiding). */
const MAX_PREP_MINUTES = 180;
/** Max minutes Google may end after the dom_event (opschonen). */
const MAX_CLEANUP_MINUTES = 180;
/** Kleine tolerantie voor afronding / sync. */
const CLOCK_TOLERANCE_MINUTES = 15;

function toMs(dt: string): number {
  return parseDateTime(dt).getTime();
}

/**
 * Google-blok hoort bij dom_event als de zaal klopt en Google de zaal
 * reserveert rond het evenement (extra tijd voor opbouw/afbouw).
 */
export function timesMatchForLink(
  domStartMs: number,
  domEndMs: number,
  googleStartMs: number,
  googleEndMs: number
): boolean {
  const clock = CLOCK_TOLERANCE_MINUTES * 60 * 1000;
  const maxPrep = MAX_PREP_MINUTES * 60 * 1000;
  const maxCleanup = MAX_CLEANUP_MINUTES * 60 * 1000;

  if (googleStartMs > domStartMs + clock) return false;
  if (googleEndMs < domEndMs - clock) return false;
  if (domStartMs - googleStartMs > maxPrep) return false;
  if (googleEndMs - domEndMs > maxCleanup) return false;
  if (googleStartMs >= domEndMs || googleEndMs <= domStartMs) return false;

  return true;
}

function eventMatchesGoogleRoom(event: CalendarItem, google: CalendarItem): boolean {
  if (google.google_calendar_id && event.rooms?.length) {
    return event.rooms.some((r) => r.google_calendar_id === google.google_calendar_id);
  }
  const loc = event.location_name ?? event.room_name;
  const gRoom = google.room_name;
  if (!loc || !gRoom) return false;
  const a = normalizeRoomName(loc);
  const b = normalizeRoomName(gRoom);
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  return false;
}

function matchScore(event: CalendarItem, google: CalendarItem): number | null {
  if (event.kind !== "event" || google.kind !== "google") return null;
  if (!eventMatchesGoogleRoom(event, google)) return null;

  const domStart = toMs(event.start_datetime);
  const domEnd = toMs(event.end_datetime);
  const gStart = toMs(google.start_datetime);
  const gEnd = toMs(google.end_datetime);

  if (!timesMatchForLink(domStart, domEnd, gStart, gEnd)) return null;

  return domStart - gStart + (gEnd - domEnd);
}

function googleItemKey(g: CalendarItem): string {
  return `${g.google_calendar_id ?? ""}|${g.start_datetime}|${g.end_datetime}|${g.title}`;
}

function createLinkedItem(event: CalendarItem, google: CalendarItem): CalendarItem {
  return {
    kind: "linked",
    id: event.id,
    title: event.title,
    start_datetime: event.start_datetime,
    end_datetime: event.end_datetime,
    google_start_datetime: google.start_datetime,
    google_end_datetime: google.end_datetime,
    room_id: null,
    room_name: google.room_name,
    room_color: google.room_color,
    location_name: event.location_name,
    category: event.category,
    description: event.description,
    google_calendar_id: google.google_calendar_id,
    linked_event_id: event.id,
    recurrence_type: event.recurrence_type,
    occurrence_date: event.occurrence_date,
    rooms: event.rooms,
  };
}

/** Start/eind voor tijdsblok in weekweergave (Google-blok bij gekoppelde items). */
export function blockDateTimeRange(item: CalendarItem): {
  start: string;
  end: string;
} {
  if (item.kind === "linked" && item.google_start_datetime && item.google_end_datetime) {
    return { start: item.google_start_datetime, end: item.google_end_datetime };
  }
  return { start: item.start_datetime, end: item.end_datetime };
}

/**
 * Koppel dom_events aan Google-agenda-items en vervang paren door één 'linked' item.
 * Ongekoppelde items blijven staan.
 */
export function mergeMatchedCalendarItems(items: CalendarItem[]): CalendarItem[] {
  const events = items.filter((i) => i.kind === "event");
  const googleItems = items.filter((i) => i.kind === "google");
  const rest = items.filter((i) => i.kind !== "event" && i.kind !== "google");

  const candidates: { event: CalendarItem; google: CalendarItem; score: number }[] = [];
  for (const event of events) {
    for (const google of googleItems) {
      const score = matchScore(event, google);
      if (score !== null) candidates.push({ event, google, score });
    }
  }
  candidates.sort((a, b) => a.score - b.score);

  const usedEvents = new Set<string>();
  const usedGoogle = new Set<string>();
  const linked: CalendarItem[] = [];

  for (const { event, google } of candidates) {
    const eventKey = `${event.id}|${event.occurrence_date ?? ""}|${event.start_datetime}`;
    if (usedEvents.has(eventKey)) continue;
    const gKey = googleItemKey(google);
    if (usedGoogle.has(gKey)) continue;
    usedEvents.add(eventKey);
    usedGoogle.add(gKey);
    linked.push(createLinkedItem(event, google));
  }

  const unmatchedEvents = events.filter(
    (e) => !usedEvents.has(`${e.id}|${e.occurrence_date ?? ""}|${e.start_datetime}`)
  );
  const unmatchedGoogle = googleItems.filter((g) => !usedGoogle.has(googleItemKey(g)));

  return [...rest, ...unmatchedEvents, ...unmatchedGoogle, ...linked];
}
